// src/pages/Payment.js
import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, Grid, Paper, CircularProgress,
  Tooltip, IconButton, Snackbar, Alert, Stack, FormControl,
  InputLabel, Select, MenuItem, Card
} from '@mui/material';
import { Add, Edit, DeleteOutline, Search as SearchIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import RightDrawer from '../../components/RightDrawer';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { GET_LISTBYID, SAVE, UPDATE, DELETE_DATA, GET_LIST } from '../../utils/api';
import dayjs from 'dayjs';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';

// currency symbols
const currencySymbols = { INR: '₹', USD: '$', JPY: '¥' };

export default function Payment() {
  // Core table & selection state
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  // Drawer & editing state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // Form state bundled
  const [formState, setFormState] = useState({
    vendorId: '',
    currency: 'INR',
    bucketCount: 1,
    buckets: [{ from: null, till: null, timePricePairs: [{ time: '', price: '' }] }],
  });

  // Delete dialog + snackbar
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [alert, setAlert] = useState({ open: false, type: 'success', message: '' });
  const [searchText, setSearchText] = useState('');
  // Vendors for dropdown
  const [vendors, setVendors] = useState([]);

  // Fetch table and vendor list
  const fetchInventory = async (searchText = '') => {
    setLoading(true);
    try {
      let queryParams = {
        ...(searchText ? { searchText } : {}),
      };
      const resp = await GET_LIST('/payment-management', queryParams);
      console.log(resp)
      setRows(resp?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  console.log(rows)
  const fetchVendors = async () => {
    try {
      const resp = await GET_LISTBYID('/vendors/list');
      setVendors(resp || []);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    fetchInventory(searchText);
    fetchVendors();
  }, []);

  // Sync editData into form whenever editor opens
  useEffect(() => {
    if (editData) {
      setFormState({
        vendorId: editData.vendor_id,
        currency: editData.currency,
        bucketCount: editData.numberOfBuckets,
        buckets: editData.pricingBuckets.map(pb => ({
          from: dayjs(pb.effective_from),
          till: dayjs(pb.effective_till),
          timePricePairs: pb.time_price_pairs,
        })),
      });
    } else {
      // reset for “Add”
      setFormState({
        vendorId: '',
        currency: 'INR',
        bucketCount: 1,
        buckets: [{ from: null, till: null, timePricePairs: [{ time: '', price: '' }] }],
      });
    }
  }, [editData]);

  // Helpers to update formState
  const updateField = (key, val) => {
    setFormState(fs => ({ ...fs, [key]: val }));
  };
  const handleBucketCountChange = e => {
    const n = parseInt(e.target.value, 10) || 0;
    setFormState(fs => ({
      ...fs,
      bucketCount: n,
      buckets: Array.from({ length: n }, (_, i) => fs.buckets[i] || {
        from: null, till: null, timePricePairs: [{ time: '', price: '' }]
      })
    }));
  };
  const handleBucketField = (i, field) => val => {
    setFormState(fs => {
      const b = [...fs.buckets];
      b[i][field] = val;
      return { ...fs, buckets: b };
    });
  };
  const handleTimePrice = (bi, ti, key) => e => {
    const val = e.target.value;
    setFormState(fs => {
      const b = [...fs.buckets];
      b[bi].timePricePairs[ti][key] = val;
      return { ...fs, buckets: b };
    });
  };

  // Open add vs edit
  const openAdd = () => setEditData(null) || setDrawerOpen(true);
  const openEdit = id => {
    const rec = rows.find(r => r.id === id);
    if (rec) {
      setEditData(rec);
      setDrawerOpen(true);
    }
  };

  // Save (new or update)
  const handleSave = async () => {
    // basic validation
    const { vendorId, buckets } = formState;
    if (!vendorId || buckets.some(b => !b.from || !b.till)) {
      return setAlert({ open: true, type: 'error', message: 'Fill required fields.' });
    }

    const payload = {
      vendor_id: formState.vendorId,
      currency: formState.currency,
      numberOfBuckets: formState.bucketCount,
      pricingBuckets: formState.buckets.map(b => ({
        effective_from: b.from.toISOString(),
        effective_till: b.till.toISOString(),
        time_price_pairs: b.timePricePairs
          .filter(tp => tp.time && tp.price)
          .map(tp => ({ time: tp.time, price: Number(tp.price) })),
      })),
      discount: editData?.discount || 0,
      createdBy: JSON.parse(localStorage.getItem('user') || '{}').email,
    };

    try {
      let res;
      if (editData?.id) {
        res = await UPDATE(`/payment-management/${editData.id}`, payload);
      } else {
        res = await SAVE('/payment-management', payload);
      }
      if ([200, 201].includes(res.status)) {
        setAlert({
          open: true,
          type: 'success',
          message: editData ? 'Updated successfully!' : 'Saved successfully!',
        });
        setDrawerOpen(false);
        await fetchInventory(searchText);
      } else {
        throw new Error();
      }
    } catch {
      setAlert({ open: true, type: 'error', message: 'Save failed.' });
    }
  };

  // Delete
  const confirmDelete = async () => {
    try {
      await DELETE_DATA('/payment-management', deleteId);
      setAlert({ open: true, type: 'success', message: 'Deleted.' });
      await fetchInventory(searchText);
    } catch {
      setAlert({ open: true, type: 'error', message: 'Delete failed.' });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // DataGrid columns
  const columns = [
    { field: 'vendorName', headerName: 'Vendor', flex: 1 },
    { field: 'numberOfBuckets', headerName: 'Baskets', flex: 1 },
    {
      field: 'effectiveFrom',
      headerName: 'From',
      flex: 1,
      renderCell: params =>
        dayjs(params.row.pricingBuckets?.[0]?.effective_from).format('YYYY-MM-DD'),
    },
    {
      field: 'effectiveTill',
      headerName: 'Till',
      flex: 1,
      renderCell: params =>
        dayjs(params.row.pricingBuckets?.[0]?.effective_till).format('YYYY-MM-DD'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: params => (
        <>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(params.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => { setDeleteId(params.id); setDeleteDialogOpen(true); }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  // add a new empty time/price pair to bucket[bIdx]
  const handleAddTimePrice = (bIdx) => {
    setFormState(prev => ({
      ...prev,
      buckets: prev.buckets.map((bucket, idx) =>
        idx !== bIdx
          ? bucket
          : {
            ...bucket,
            timePricePairs: [
              ...bucket.timePricePairs,
              { time: '', price: '' },
            ],
          }
      ),
    }));
  };

  // remove the tpIdx-th pair from bucket[bIdx]
  const handleRemoveTimePrice = (bIdx, tpIdx) => {
    setFormState(prev => ({
      ...prev,
      buckets: prev.buckets.map((bucket, idx) =>
        idx !== bIdx
          ? bucket
          : {
            ...bucket,
            timePricePairs: bucket.timePricePairs.filter(
              (_tp, i) => i !== tpIdx
            ),
          }
      ),
    }));
  };



  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Payment Management</Typography>
      <Typography variant="body2" color="gray" sx={{ mb: 1 }}>
        Last Updated: {new Date().toLocaleDateString()}
      </Typography>

      <Grid container justifyContent="flex-end" spacing={1} sx={{ mb: 1 }}>
        <Grid item>
          <TextField
            id="outlined-size-small"
            label="Search"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchInventory(searchText);
              }
            }}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'gray', mr: 1 }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        </Grid>
        {/* <Grid item>
          <DatePicker
            label="Filter date"
            slotProps={{ textField: { size: 'small', sx: { p: 0 } } }}
          />
        </Grid> */}
        <Grid item>
          <Button startIcon={<Add />} variant='contained' size="small" onClick={openAdd}>Add Pricing</Button>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 'calc(100vh - 190px)', width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              sortingOrder={['asc', 'desc']}
              checkboxSelection
              disableRowSelectionOnClick
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
              }
              onSelectionModelChange={setSelected}
              selectionModel={selected}
              sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f2f2f2' } }}
            />
          </Box>
        )}
      </Paper>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        message="Delete this record?"
      />

      <RightDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        title={editData ? 'Edit Pricing' : 'Add Pricing'}
      >
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Vendor</InputLabel>
            <Select
              value={formState.vendorId}
              label="Vendor"
              onChange={e => updateField('vendorId', e.target.value)}
            >
              {vendors.map(v => (
                <MenuItem key={v._id} value={v._id}>
                  {v.vendor_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Currency</InputLabel>
            <Select
              value={formState.currency}
              label="Currency"
              onChange={e => updateField('currency', e.target.value)}
            >
              {Object.entries(currencySymbols).map(([k, sym]) => (
                <MenuItem key={k} value={k}>{k}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Number of Buckets"
            type="number"
            size="small"
            fullWidth
            value={formState.bucketCount}
            onChange={handleBucketCountChange}
          />

          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {formState.buckets.map((b, bucketIdx) => (
              <Card
                key={bucketIdx}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: '#f5f5f5',
                  border: '1px solid #ccc',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Bucket {bucketIdx + 1}
                </Typography>

                {/* Date pickers */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <DatePicker
                      label="From"
                      value={b.from}
                      onChange={handleBucketField(bucketIdx, 'from')}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      label="Till"
                      value={b.till}
                      onChange={handleBucketField(bucketIdx, 'till')}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Grid>
                </Grid>

                {/* Time/Price rows */}
                {b.timePricePairs.map((tp, tpIdx) => (
                  <Box
                    key={tpIdx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <TextField
                      label="Time"
                      size="small"
                      sx={{ flex: 1 }}
                      value={tp.time}
                      onChange={handleTimePrice(bucketIdx, tpIdx, 'time')}
                    />
                    <TextField
                      label={`Price (${currencySymbols[formState.currency]})`}
                      size="small"
                      sx={{ flex: 1 }}
                      value={tp.price}
                      onChange={handleTimePrice(bucketIdx, tpIdx, 'price')}
                    />

                    {/* Add another time/price pair */}
                    {tpIdx === b.timePricePairs.length - 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleAddTimePrice(bucketIdx)}
                      >
                        <AddCircleOutline />
                      </IconButton>
                    )}

                    {/* Remove this time/price pair */}
                    {b.timePricePairs.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveTimePrice(bucketIdx, tpIdx)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Card>
            ))}
          </Box>

        </Stack>
      </RightDrawer>

      <Snackbar
        open={alert.open}
        autoHideDuration={3000}
        onClose={() => setAlert(a => ({ ...a, open: false }))}
      >
        <Alert severity={alert.type}>{alert.message}</Alert>
      </Snackbar>
    </Box>
  );
}
